import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { leadsApi, Lead } from '@/api/leads';
import { billingApi } from '@/api/billing';
import { hospitalAdminApi } from '@/api/hospitalAdmin';
import { useAuth } from '@/contexts/AuthContext';
import { useBookingHospitals } from '@/hooks/useBookingHospitals';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FilterMultiSelect } from '@/components/ui/filter-multi-select';
import {
  formatLeadStatusLabel,
  formatLeadSubStatusLabel,
} from '@/lib/crmLeadStyles';
import { LeadDetailModal } from '@/components/crm/LeadDetailModal';
import { isExternalPartner } from '@/utils/crmAccess';
import { toast } from 'sonner';

const IST: Intl.DateTimeFormatOptions = { timeZone: 'Asia/Kolkata' };

function formatIstDate(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', IST);
}

function formatIstDateTime(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function campaignOf(l: Lead): string {
  return (
    l.campaignLabel?.trim() ||
    l.campaignName?.trim() ||
    l.googleAdsUtmCampaign?.trim() ||
    ''
  );
}

function hospitalLabel(l: Lead): string {
  return l.opdHospitalName?.trim() || l.hospitalName?.trim() || '—';
}

function doctorLabel(l: Lead): string {
  return l.opdDoctorName?.trim() || l.specialisation?.trim() || '';
}

function opdPaidColumnLabel(l: Lead): 'Free OPD' | 'Paid' | null {
  if (l.opdConsultationFree) return 'Free OPD';
  if (l.opdConsultationPaid) return 'Paid';
  return null;
}

function csvEscape(v: string | number | null | undefined): string {
  const s = v == null ? '' : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default function CrmIpdCasesPage() {
  const { user, isChainAdmin } = useAuth();
  const [search, setSearch] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('__all__');
  const [hospitalFilter, setHospitalFilter] = useState('__all__');
  const [doctorFilter, setDoctorFilter] = useState('__all__');
  const [sourceFilters, setSourceFilters] = useState<string[]>([]);
  const [campaignFilters, setCampaignFilters] = useState<string[]>([]);
  const [opdFrom, setOpdFrom] = useState('');
  const [opdTo, setOpdTo] = useState('');
  const [surgeryFrom, setSurgeryFrom] = useState('');
  const [surgeryTo, setSurgeryTo] = useState('');
  const [updatedFrom, setUpdatedFrom] = useState('');
  const [updatedTo, setUpdatedTo] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [leadModalAnchor, setLeadModalAnchor] = useState<Lead | null>(null);
  const [openingOpdBillId, setOpeningOpdBillId] = useState<string | null>(null);

  const isPartner = isExternalPartner(user);
  const isSalesManager = user?.role === 'SALES_MANAGER';
  const canFilterByOwner =
    !isPartner &&
    (user?.role === 'HOSPITAL_ADMIN' ||
      user?.role === 'SUPER_ADMIN' ||
      isSalesManager);

  const showHospitalColumn = !isPartner;
  const { hospitals: bookingHospitals } = useBookingHospitals();
  const hospitalFilterOptions = useMemo(() => {
    if (isChainAdmin && (user?.hospitals?.length ?? 0) > 0) {
      return user!.hospitals!.map((h) => ({ id: h.id, name: h.name }));
    }
    if (bookingHospitals.length > 0) return bookingHospitals;
    return [];
  }, [isChainAdmin, user?.hospitals, bookingHospitals]);
  const showHospitalFilter = !isPartner && hospitalFilterOptions.length > 1;

  const enabled =
    !!user &&
    (user.role === 'SUPER_ADMIN' || !!user.hospitalId);
  const listParams = useMemo(
    () => ({
      page,
      pageSize,
      search: search.trim() || undefined,
      ownerId: ownerFilter,
      hospitalId: hospitalFilter !== '__all__' ? hospitalFilter : undefined,
      doctor: doctorFilter,
      sources: sourceFilters.length ? sourceFilters.join(',') : undefined,
      campaigns: campaignFilters.length ? campaignFilters.join(',') : undefined,
      opdFrom: opdFrom || undefined,
      opdTo: opdTo || undefined,
      surgeryFrom: surgeryFrom || undefined,
      surgeryTo: surgeryTo || undefined,
      updatedFrom: updatedFrom || undefined,
      updatedTo: updatedTo || undefined,
    }),
    [
      page,
      search,
      ownerFilter,
      hospitalFilter,
      doctorFilter,
      sourceFilters,
      campaignFilters,
      opdFrom,
      opdTo,
      surgeryFrom,
      surgeryTo,
      updatedFrom,
      updatedTo,
    ]
  );

  const { data: ipdPage, isLoading: ipdLoading } = useQuery({
    queryKey: ['crm-ipd-cases', user?.id, isChainAdmin ? 'chain' : user?.hospitalId, listParams],
    queryFn: async () => (await leadsApi.listSidebarIpdCases(listParams)).data.data,
    enabled,
    staleTime: 30_000,
  });
  const items = (ipdPage?.items ?? []) as Lead[];
  const total = ipdPage?.total ?? items.length;

  const { data: staffOptions = [] } = useQuery({
    queryKey: ['crm-ipd-staff-options', user?.hospitalId, user?.id, isSalesManager],
    queryFn: async () => {
      const rows = await hospitalAdminApi.getStaff();
      const salesPeople = (rows || []).filter(
        (s: { role?: string; status?: string; reportsToManagerId?: string | null; id?: string }) => {
          if (s?.role !== 'SALES_PERSON' || (s?.status ?? 'ACTIVE') !== 'ACTIVE') return false;
          if (isSalesManager) {
            return s.reportsToManagerId === user?.id || s.id === user?.id;
          }
          return true;
        },
      );
      if (isSalesManager && user?.id) {
        const self = (rows || []).find((s: { id?: string }) => s.id === user.id);
        if (self && !salesPeople.some((s: { id?: string }) => s.id === user.id)) {
          return [self, ...salesPeople];
        }
      }
      return salesPeople;
    },
    enabled: enabled && canFilterByOwner,
    staleTime: 60_000,
  });

  const { data: leadFilterOptions } = useQuery({
    queryKey: ['lead-filter-options', user?.hospitalId],
    queryFn: () => leadsApi.getLeadFilterOptions(),
    enabled,
    staleTime: 60_000,
  });

  const sourceFilterOptions = useMemo(
    () =>
      (leadFilterOptions?.sources ?? []).map((s) => ({
        value: s,
        label: s,
      })),
    [leadFilterOptions?.sources]
  );

  const campaignFilterOptions = useMemo(
    () =>
      (leadFilterOptions?.campaignNames ?? []).map((c) => ({
        value: c,
        label: c,
      })),
    [leadFilterOptions?.campaignNames]
  );

  const doctorFilterOptions = useMemo(() => {
    const names = new Set<string>();
    for (const l of items) {
      const name = doctorLabel(l);
      if (name) names.add(name);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = items;

  const hasDateFilters =
    opdFrom || opdTo || surgeryFrom || surgeryTo || updatedFrom || updatedTo;

  const clearDateFilters = () => {
    setPage(1);
    setOpdFrom('');
    setOpdTo('');
    setSurgeryFrom('');
    setSurgeryTo('');
    setUpdatedFrom('');
    setUpdatedTo('');
  };

  const resetFilters = () => {
    setPage(1);
    setSearch('');
    setOwnerFilter('__all__');
    setHospitalFilter('__all__');
    setDoctorFilter('__all__');
    setSourceFilters([]);
    setCampaignFilters([]);
    clearDateFilters();
  };

  const downloadCsv = () => {
    if (!opdFrom || !opdTo) {
      toast.error('Set OPD visited date from and to before downloading.');
      return;
    }
    if (filtered.length === 0) {
      toast.error('No rows in the selected date range to download.');
      return;
    }
    const headers = [
      'Patient',
      ...(isPartner ? [] : ['Phone']),
      'Doctor',
      ...(showHospitalColumn ? ['Hospital'] : []),
      'Owner',
      'Source',
      'Campaign',
      'Lead status',
      'Sub status',
      'Paid',
      'OPD visited date',
      'Lead updated',
      'Surgery date',
      ...(isPartner ? [] : ['Notes']),
    ];
    const lines = [headers.map(csvEscape).join(',')];
    for (const l of filtered) {
      lines.push(
        [
          l.patientName,
          ...(isPartner ? [] : [l.phone]),
          l.opdDoctorName?.trim() || l.specialisation?.trim() || '',
          ...(showHospitalColumn ? [hospitalLabel(l) === '—' ? '' : hospitalLabel(l)] : []),
          l.assignedTo?.name ?? 'Unassigned',
          l.source || '',
          campaignOf(l),
          formatLeadStatusLabel(l.status),
          l.leadSubStatus ? formatLeadSubStatusLabel(l.leadSubStatus) : '',
          opdPaidColumnLabel(l) ?? '',
          formatIstDate(l.opdVisitedAt ?? l.opdDisplayAt),
          formatIstDateTime(l.leadUpdatedAt ?? l.updatedAt),
          formatIstDate(l.tentativeSurgeryDate),
          ...(isPartner ? [] : [l.notes ?? '']),
        ]
          .map(csvEscape)
          .join(',')
      );
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ipd-cases-${opdFrom}-to-${opdTo}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
    toast.success(`Downloaded ${filtered.length} row(s)`);
  };

  return (
    <div className="p-6 space-y-4">
      <LeadDetailModal
        open={leadModalOpen}
        onOpenChange={(o) => {
          setLeadModalOpen(o);
          if (!o) setLeadModalAnchor(null);
        }}
        anchorLead={leadModalAnchor}
        navLeads={filtered}
      />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">IPD Cases</h1>
          <p className="text-xs text-slate-500 mt-1">
            {isPartner
              ? 'Meta and Google campaign outcomes only — OPD visited and IPD pipeline. Patient phone numbers are not shown.'
              : isSalesManager
                ? 'Your team’s CRM leads: follow-up pipeline, OPD visited, and IPD conversions (IPD admitted / procedure / day care). Filter by salesperson, source, or dates.'
                : 'Follow-up awaiting stages, OPD booked (paid), OPD visited, and IPD conversions from CRM. Filter by owner, source, campaign, dates, or search by patient.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder={isPartner ? 'Search by patient name' : 'Search by name or phone'}
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="w-72 h-9"
          />
          <Button type="button" variant="outline" className="h-9 gap-1.5" onClick={downloadCsv}>
            <Download className="h-4 w-4" />
            Download CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-slate-50/80 px-3 py-3">
        {canFilterByOwner ? (
          <div>
            <Label className="text-[11px] text-slate-500">Owner</Label>
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-[12rem] h-9 text-sm bg-white">
                <SelectValue placeholder="All owners" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="__all__">All owners</SelectItem>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {staffOptions.map((s: { id: string; name: string }) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        {showHospitalFilter ? (
          <div>
            <Label className="text-[11px] text-slate-500">Hospital</Label>
            <Select value={hospitalFilter} onValueChange={setHospitalFilter}>
              <SelectTrigger className="w-[13rem] h-9 text-sm bg-white">
                <SelectValue placeholder="All hospitals" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="__all__">All hospitals</SelectItem>
                {hospitalFilterOptions.map((h) => (
                  <SelectItem key={h.id} value={h.id}>
                    {h.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        {!isPartner ? (
          <div>
            <Label className="text-[11px] text-slate-500">Doctor</Label>
            <Select value={doctorFilter} onValueChange={setDoctorFilter}>
              <SelectTrigger className="w-[12rem] h-9 text-sm bg-white">
                <SelectValue placeholder="All doctors" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value="__all__">All doctors</SelectItem>
                {doctorFilterOptions.map((name) => (
                  <SelectItem key={name} value={name}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        {!isPartner ? (
          <>
            <div>
              <Label className="text-[11px] text-slate-500">Source</Label>
              <FilterMultiSelect
                values={sourceFilters}
                onValuesChange={setSourceFilters}
                options={sourceFilterOptions}
                placeholder="Source"
                allLabel="All sources"
                triggerClassName="w-[11rem] bg-white"
              />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Campaign</Label>
              <FilterMultiSelect
                values={campaignFilters}
                onValuesChange={setCampaignFilters}
                options={campaignFilterOptions}
                placeholder="Campaign"
                allLabel="All campaigns"
                triggerClassName="w-[12rem] bg-white"
              />
            </div>
          </>
        ) : null}

        <div>
          <Label className="text-[11px] text-slate-500">OPD visited from</Label>
          <Input
            type="date"
            value={opdFrom}
            onChange={(e) => setOpdFrom(e.target.value)}
            className="w-[9.5rem] h-9 bg-white"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">OPD visited to</Label>
          <Input
            type="date"
            value={opdTo}
            onChange={(e) => setOpdTo(e.target.value)}
            className="w-[9.5rem] h-9 bg-white"
          />
        </div>

        <div>
          <Label className="text-[11px] text-slate-500">Surgery date from</Label>
          <Input
            type="date"
            value={surgeryFrom}
            onChange={(e) => setSurgeryFrom(e.target.value)}
            className="w-[9.5rem] h-9 bg-white"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Surgery date to</Label>
          <Input
            type="date"
            value={surgeryTo}
            onChange={(e) => setSurgeryTo(e.target.value)}
            className="w-[9.5rem] h-9 bg-white"
          />
        </div>

        <div>
          <Label className="text-[11px] text-slate-500">Lead updated from</Label>
          <Input
            type="date"
            value={updatedFrom}
            onChange={(e) => setUpdatedFrom(e.target.value)}
            className="w-[9.5rem] h-9 bg-white"
          />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Lead updated to</Label>
          <Input
            type="date"
            value={updatedTo}
            onChange={(e) => setUpdatedTo(e.target.value)}
            className="w-[9.5rem] h-9 bg-white"
          />
        </div>

        {hasDateFilters ||
        sourceFilters.length ||
        campaignFilters.length ||
        hospitalFilter !== '__all__' ||
        doctorFilter !== '__all__' ? (
          <Button
            type="button"
            variant="outline"
            className="h-9"
            onClick={resetFilters}
          >
            Clear filters
          </Button>
        ) : null}
      </div>

      <div className="rounded-xl border bg-white shadow-sm overflow-hidden">
        <div className="max-h-[75vh] overflow-y-auto">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr className="text-xs text-slate-500 border-b">
                <th className="px-3 py-2 text-left font-medium">Patient</th>
                <th className="px-3 py-2 text-left font-medium">Doctor</th>
                {showHospitalColumn ? (
                  <th className="px-3 py-2 text-left font-medium">Hospital</th>
                ) : null}
                <th className="px-3 py-2 text-left font-medium">Owner</th>
                <th className="px-3 py-2 text-left font-medium">Source</th>
                <th className="px-3 py-2 text-left font-medium">Campaign</th>
                <th className="px-3 py-2 text-left font-medium">Lead status</th>
                <th className="px-3 py-2 text-left font-medium">Paid</th>
                <th className="px-3 py-2 text-left font-medium">OPD visited</th>
                <th className="px-3 py-2 text-left font-medium">Lead updated</th>
                <th className="px-3 py-2 text-left font-medium">Surgery date</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {ipdLoading ? (
                <tr>
                  <td colSpan={showHospitalColumn ? 12 : 11} className="px-3 py-8 text-center text-slate-500">
                    Loading IPD cases…
                  </td>
                </tr>
              ) : null}
              {!ipdLoading &&
              filtered.map((l: Lead) => {
                const tDate = formatIstDate(l.tentativeSurgeryDate) || '—';
                const campaign = campaignOf(l);
                const visitedLabel = formatIstDate(l.opdVisitedAt ?? l.opdDisplayAt) || '—';
                const updatedRaw = l.leadUpdatedAt ?? l.updatedAt;
                const updatedLabel = formatIstDateTime(updatedRaw) || '—';
                const doctorLabel =
                  l.opdDoctorName?.trim() || l.specialisation?.trim() || '—';
                const statusLabel = formatLeadStatusLabel(l.status);
                const subLabel = l.leadSubStatus
                  ? formatLeadSubStatusLabel(l.leadSubStatus)
                  : null;
                return (
                  <tr key={l.id} className="border-b last:border-b-0 hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{l.patientName}</div>
                      {!isPartner && (l.phone || l.alternatePhone) ? (
                        <div className="text-xs text-slate-500">
                          {l.phone || '—'}
                          {l.alternatePhone?.trim() ? (
                            <span className="text-slate-400"> · alt {l.alternatePhone.trim()}</span>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-slate-700 max-w-[9rem] truncate" title={doctorLabel}>
                      {doctorLabel}
                    </td>
                    {showHospitalColumn ? (
                      <td
                        className="px-3 py-2 text-slate-700 max-w-[10rem] truncate"
                        title={hospitalLabel(l)}
                      >
                        {hospitalLabel(l)}
                      </td>
                    ) : null}
                    <td className="px-3 py-2 text-slate-700">{l.assignedTo?.name ?? 'Unassigned'}</td>
                    <td className="px-3 py-2 text-slate-700">{l.source || '—'}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-[10rem] truncate" title={campaign || undefined}>
                      {campaign || '—'}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      <div className="font-medium">{statusLabel}</div>
                      {subLabel ? (
                        <div className="text-xs text-slate-500 mt-0.5">{subLabel}</div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {(() => {
                        const paidLabel = opdPaidColumnLabel(l);
                        if (!paidLabel) return '—';
                        const isFree = paidLabel === 'Free OPD';
                        const badgeClass = isFree
                          ? 'bg-sky-50 text-sky-900 ring-sky-600/20 hover:bg-sky-100'
                          : 'bg-emerald-50 text-emerald-800 ring-emerald-600/20 hover:bg-emerald-100';
                        if (!isPartner && l.opdConsultationBillId) {
                          return (
                          <button
                            type="button"
                            disabled={openingOpdBillId === l.opdConsultationBillId}
                            title="Open OPD bill (download from the PDF viewer)"
                            className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset disabled:opacity-60 cursor-pointer disabled:cursor-wait ${badgeClass}`}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const billId = l.opdConsultationBillId;
                              if (!billId) return;
                              setOpeningOpdBillId(billId);
                              try {
                                const res = await billingApi.exportBillPDF(billId);
                                const blob = new Blob([res.data as BlobPart], { type: 'application/pdf' });
                                const ct = String(res.headers?.['content-type'] ?? '');
                                if (
                                  res.status !== 200 ||
                                  (!ct.includes('application/pdf') && blob.type !== 'application/pdf')
                                ) {
                                  const text = await blob.text();
                                  let msg = 'Could not open bill';
                                  try {
                                    const j = JSON.parse(text);
                                    if (j?.message) msg = j.message;
                                  } catch {
                                    /* ignore */
                                  }
                                  toast.error(msg);
                                  return;
                                }
                                const url = URL.createObjectURL(blob);
                                const win = window.open(url, '_blank', 'noopener,noreferrer');
                                if (!win) {
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `OPD-bill-${billId.slice(0, 8)}.pdf`;
                                  a.rel = 'noopener';
                                  document.body.appendChild(a);
                                  a.click();
                                  a.remove();
                                  toast.message('PDF downloaded (allow pop-ups to open in a new tab)');
                                }
                                setTimeout(() => URL.revokeObjectURL(url), 120_000);
                              } catch (err: unknown) {
                                let message = 'Failed to open OPD bill';
                                const data = (err as { response?: { data?: unknown } })?.response?.data;
                                if (data instanceof Blob) {
                                  try {
                                    const t = await data.text();
                                    const j = JSON.parse(t);
                                    if (j?.message) message = j.message;
                                  } catch {
                                    /* ignore */
                                  }
                                } else if (
                                  data &&
                                  typeof data === 'object' &&
                                  'message' in data &&
                                  typeof (data as { message: unknown }).message === 'string'
                                ) {
                                  message = (data as { message: string }).message;
                                }
                                toast.error(message);
                              } finally {
                                setOpeningOpdBillId(null);
                              }
                            }}
                          >
                            {openingOpdBillId === l.opdConsultationBillId ? 'Opening…' : paidLabel}
                          </button>
                          );
                        }
                        return (
                          <span
                            className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${badgeClass}`}
                          >
                            {paidLabel}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{visitedLabel}</td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap text-xs">{updatedLabel}</td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{tDate}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8"
                        onClick={() => {
                          setLeadModalAnchor(l);
                          setLeadModalOpen(true);
                        }}
                      >
                        Open lead
                      </Button>
                    </td>
                  </tr>
                );
              })}
              {!ipdLoading && filtered.length === 0 && (
                <tr>
                  <td colSpan={showHospitalColumn ? 12 : 11} className="px-3 py-8 text-center text-slate-500">
                    No IPD cases found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!ipdLoading && total > pageSize ? (
          <div className="flex items-center justify-between border-t px-3 py-2 text-sm text-slate-600">
            <span>
              Page {page} · {total} total
            </span>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page * pageSize >= total}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
